import { getLogger } from '@confluence/logger';

import { type Match } from './RouteManager';

type TransitionCallback = (args: OnlyCalledIfFuncArgs) => Promise<void>;
type OnlyCalledIfFuncArgs = { match: Match };
type OnlyCalledIfFunc = (args: OnlyCalledIfFuncArgs) => boolean;
type RejectPromiseFunc = (reason?: any) => void;
type ActiveCallbackPromise = { promise: Promise<void>; reject: RejectPromiseFunc };

export class ExternalRejectionError extends Error {
	constructor(message?: string) {
		super(message);
		this.name = 'ExternalRejectionError';
	}
}

// TODO MODES-6531 Implement timeouts into preloader callback promises. If promise takes too long it should be rejected so it doesn't indefinitely block route changes.
/**
 * GlobalTransitionPreloader is responsible for managing transition callbacks during route changes.
 * These callbacks can be useful if you need to execute some preloading logic before certain route transitions finish, e.g. live doc -> live doc transitions.
 * It ensures that callbacks are executed only when necessary and prevents outdated route changes
 * from being triggered due to quick successive navigations.
 *
 * This class maintains a list of registered callbacks, their unique identifiers, and conditions
 * under which they should be executed. It also handles the lifecycle of promises associated with
 * these callbacks to ensure proper synchronization and error handling.
 */
export class GlobalTransitionPreloader {
	private logger = getLogger('GlobalTransitionPreloader');
	private callbacks: Map<string, [TransitionCallback, OnlyCalledIfFunc | undefined]> = new Map();
	private activelyRunningCallbackPromises: Record<string, ActiveCallbackPromise> = {};
	private latestPathname: string = '';

	/**
	 * Registers a transition callback that will be awaited on a route change.
	 * The callback will only be invoked if the `onlyCalledIfFunc` returns true or if `onlyCalledIfFunc` is not provided. e.g. To check that a contentId is what you expect it should be. `onlyCalledIfFunc` should be very performant as it will run every route change.
	 *
	 * PERFORMANCE WARNING: `onlyCalledIfFunc` is executed every route change, and `callback` promise is awaited every time `onlyCalledIfFunc` returns `true` (or if it doesn't exist).
	 *                      If either of these functions are not very performant, it could cause undesirable performance degradation on every transition.
	 *                      Try to avoid any time-intensive invocations, especially in `onlyCalledIfFunc`. `onlyCalledIfFunc` ideally only runs a condition the `match` that it's passed, e.g. to check `match.params.contentId` or `match.pathname`.
	 *                      BEFORE REGISTERING A NEW TRANSITION CALLBACK, you must be confident that the negative performance impact is acceptable.
	 *
	 * @example registerTransitionCallback({
	 *     id: 'unique-id-related-to-my-code',
	 *     callback: ({match}) => {
	 *         return new Promise((resolve) => {
	 *             const contentId = match.params?.contentId;
	 *             // do logic that is a hard pre-requisite for next route and cannot be lazy/concurrently loaded, ideally shouldn't be slow
	 *             resolve(); // route change gets unblocked after these callback promises are resolved
	 *         }
	 *     },
	 *     onlyCalledIfFunc: ({match}) => {
	 *         const contentId = match.params?.contentId;
	 *         // do some very quick logic, ideally no logic that we need to wait for, this function is run every route change
	 *         if (isLiveDoc(contentId)) {
	 *             return true;
	 *         } else {
	 *             return false;
	 *         }
	 *     }
	 * });
	 *
	 * @param {Object} params - The parameters for the transition callback.
	 * @param {string} params.id - A unique identifier to prevent duplicate callback registrations.
	 * @param {TransitionCallback} params.callback - The callback function to be executed during a route transition. Must return a promise.
	 * @param {OnlyCalledIfFunc} [params.onlyCalledIfFunc] - A function that determines whether the callback should be called.
	 */
	registerTransitionCallback({
		id,
		callback,
		onlyCalledIfFunc,
	}: {
		id: string;
		callback: TransitionCallback;
		onlyCalledIfFunc?: OnlyCalledIfFunc;
	}): void {
		// We want to keep transition callbacks unique to guarantee we aren't duplicating work, so we tie callbacks to an ID. If there are multiple registrations of the same callback ID, we ignore registration attempts if we're already registered.
		if (this.callbacks.has(id)) {
			return;
		}

		this.callbacks.set(id, [callback, onlyCalledIfFunc]);
	}

	/**
	 * Processes transition callbacks during a route change, checking if we need to create promises from callbacks, and waiting for them to resolve/reject before continuing the route change.
	 *
	 * @param {OnlyCalledIfFuncArgs & { onRouteContinue: () => void }} args - The arguments containing the route `match` object and the `onRouteContinue` continuation function.
	 *                                                                        `onRouteContinue` is called once promises resolve and should finish executing the route change.
	 * @returns {boolean} - Returns `true` if processing transition callback and routeChangeHandler should not continue running.
	 *                      Returns `false` if not processing transition callback and routeChangeHandler should continue running.
	 */
	processTransitionCallback({
		match,
		onRouteContinue,
	}: OnlyCalledIfFuncArgs & { onRouteContinue: () => void }): boolean {
		try {
			if (!this.callbacks.size) {
				return false;
			}

			// Sometimes the same pathname is processed by routeChangeHandler twice in quick succession, once for PUSH action and then for REPLACE action.
			// This ensures that the REPLACE action doesn't skip over or delay the callback promise started by the PUSH action of the same pathname; it'll rely on the existing activelyRunningCallbackPromises from the first route change.
			const previousPathname = this.latestPathname;
			this.latestPathname = match?.pathname;
			if (this.latestPathname !== previousPathname) {
				// Cancel all currently running promises because we're going to a different route. Helps prevent multiple quick route changes from causing race condition that could send user to outdated route change.
				// If needed, `getCallbackPromises` below will create new promises for the latest route.
				Object.entries(this.activelyRunningCallbackPromises).forEach(([callbackId, { reject }]) => {
					reject();

					// Delete the active running promise to ensure a rejected promise isn't returned by `this.getCallbackPromises`, instead it should create a new one for this callbackId
					delete this.activelyRunningCallbackPromises[callbackId];
				});
			}

			const { callbackPromises, callbackPromiseIds } = this.getCallbackPromises({ match });
			if (!callbackPromises.length) {
				// If there are no callback promises, route change will immediately continue. Reset our callbacks state as we don't want registered callbacks to linger once the user leaves the route. Callbacks should be registered on page/etc load if needed.
				// EDGE CASE: If the user is on a live doc and quickly clicks a non-live-doc like a view page, then quickly clicks back into a live doc before the view page has loaded, the preload-live-doc callback may have been removed, slowing down load time into that live doc.
				//            One alternative is to always check if we should preload a live doc on any route change by never unregistering that callback, but the performance impact doesn't seem worth it.
				this.callbacks = new Map();
				this.activelyRunningCallbackPromises = {};

				return false;
			}

			// Await all the promises and then send the user to the next route if appropriate.
			void Promise.allSettled(callbackPromises).then((promiseResults) => {
				let externallyRejectedCount = 0;
				promiseResults.forEach((result) => {
					if (result.status === 'rejected') {
						if (result.reason instanceof ExternalRejectionError) {
							externallyRejectedCount++;
						} else {
							this.logger
								.error`There was an error with a transition callback promise: ${result.reason}, active callbackPromiseIds: ${callbackPromiseIds.join(',')}`;
						}
					}
				});

				// If all promises have been externally rejected, that means these promises are for an outdated route change, the user has since changed routes again, so we don't want to redirect the user to the route.
				// This avoids race condition scenarios where user goes from Doc A -> Doc B -> Doc C, but ends up on Doc B because its promises finished after Doc C's promises.
				if (externallyRejectedCount < callbackPromises.length) {
					// On successful next route, we clear the currently registered callbacks. Callbacks should be registered on page/etc load if needed.
					this.callbacks = new Map();
					this.activelyRunningCallbackPromises = {};

					onRouteContinue();
				}
			});

			return true;
		} catch (error) {
			this.logger
				.error`There was an error running processTransitionCallback: ${error}, registered callbackIds: ${Array.from(this.callbacks.keys()).join(',')}`;
		}

		return false;
	}

	/**
	 * Creates an array of promises from registered callbacks that should be executed
	 * during a route transition. This method checks conditions under which each callback
	 * should be called and manages the lifecycle of the promises.
	 *
	 * @private
	 * @param { match: Match } params - The parameters for the method, containing the match object.
	 * @returns { callbackPromises: Array<Promise<void>>, callbackPromiseIds: Array<string> } An object containing an array of promises and their corresponding IDs.
	 */
	private getCallbackPromises({ match }: { match: Match }): {
		callbackPromises: Array<Promise<void>>;
		callbackPromiseIds: Array<string>;
	} {
		if (!this.callbacks) return { callbackPromises: [], callbackPromiseIds: [] };

		const callbackPromises: Array<Promise<void>> = [];
		const callbackPromiseIds: Array<string> = []; // Recommendation is to use this to help provide context to promise errors
		this.callbacks.forEach(([callback, onlyCalledIfFunc], callbackId) => {
			// Call our `onlyCalledIfFunc` if it's set, we're only waiting for this callback promise if it's `true` or if it's not set.
			const isCallAllowed = onlyCalledIfFunc ? onlyCalledIfFunc({ match }) : true;

			// If we want to trigger a callback promise, return the already running one if we have it, otherwise create a new one.
			if (isCallAllowed) {
				const activeRunningPromise = this.activelyRunningCallbackPromises[callbackId];
				if (activeRunningPromise?.promise) {
					callbackPromises.push(activeRunningPromise.promise);
					callbackPromiseIds.push(callbackId);
				} else {
					// Start the promise and store the promise and its reject function in state (this.processTransitionCallback may call `reject` if the user changes route)
					let callbackPromiseReject: RejectPromiseFunc;
					const callbackPromise = new Promise<void>((resolve, reject) => {
						callbackPromiseReject = reject;
						callback({ match }).then(resolve).catch(reject);
					});
					callbackPromises.push(callbackPromise);
					callbackPromiseIds.push(callbackId);
					this.activelyRunningCallbackPromises[callbackId] = {
						promise: callbackPromise,
						reject: (reason) => callbackPromiseReject(new ExternalRejectionError(reason)),
					};
				}
			} else if (this.activelyRunningCallbackPromises[callbackId]) {
				// If the promise no longer needs to run then stop tracking it if it's already running.
				this.activelyRunningCallbackPromises[callbackId]?.reject?.();
				delete this.activelyRunningCallbackPromises[callbackId];
			}
		});
		return { callbackPromises, callbackPromiseIds };
	}
}
